import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { DollarSign, FileSearch, FileX } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ModalGeneric } from "@/components/utils/ModalGeneric";
import NotasFiscaisService from "@/services/NotasFiscaisService";
import { naturezaOperacao } from "@/mock/GerarNotasOptions";
import ViewNfseDetail from "@/pages/UserGoOn/NotasFiscais/sections/ViewNfseDetail";
import GetAllClientes from "@/services/getClientes";
import ModalPreviaNotaFiscal, {
  type PreviewData,
} from "./ModalPreviaNotaFiscal";

interface Props {
  id_desk: string;
  variant?: "button" | "icon";
  ref_nfe?: string | null;
  status_nfe?: string | null;
  onSuccess?: () => void;
  deskData?: any;
}

type CnaeApi = { codigo: number; descricao: string };
type SelectOption = { value: string; label: string };

type Inputs = {
  natureza_operacao: any;
  iss_retido: boolean;
  valor_ir: string;
  valor_pis: string;
  valor_cofins: string;
  valor_csll: string;
  codigo_cnae: SelectOption | null;
  informacoes_adicionais_contribuinte: string;
  item_lista_servico: SelectOption | null;
  justificativa_cancelamento: string;
};

type NfeResponse = {
  data: any[];
  meta: {
    pageNumber: number;
    pageSize: number;
    total: number;
    lastPage: number;
  };
};

export default function GerarNotaFiscal({
  id_desk,
  variant = "button",
  ref_nfe,
  status_nfe,
  onSuccess,
  deskData,
}: Props) {
  const queryClient = useQueryClient();

  const [refLocal, setRefLocal] = useState<string | null>(null);
  const [statusLocal, setStatusLocal] = useState<string | null>(null);

  const normalizeStatus = (s?: string | null) =>
    String(s ?? "")
      .trim()
      .toLowerCase();

  const statusEfetivo = useMemo(
    () => normalizeStatus(statusLocal ?? status_nfe),
    [statusLocal, status_nfe],
  );

  useEffect(() => {
    const incoming = normalizeStatus(status_nfe);
    if (!incoming) return;

    const local = normalizeStatus(statusLocal);

    const isLocalLoading =
      local.includes("carreg") || local.includes("process");
    const isIncomingFinal =
      incoming.includes("erro") ||
      incoming.includes("reje") ||
      incoming.includes("neg") ||
      incoming.includes("falha") ||
      incoming.includes("emit") ||
      incoming.includes("autoriz") ||
      incoming.includes("cancel");

    if (isLocalLoading && isIncomingFinal) {
      setStatusLocal(incoming);
    }
  }, [status_nfe]);

  const refEfetiva = useMemo(
    () => refLocal ?? ref_nfe ?? null,
    [refLocal, ref_nfe],
  );

  const hasStatusReal = useMemo(() => {
    if (!statusEfetivo) return false;

    if (
      statusEfetivo === "null" ||
      statusEfetivo === "undefined" ||
      statusEfetivo === "-" ||
      statusEfetivo === "pendente"
    ) {
      return false;
    }

    return true;
  }, [statusEfetivo]);

  const hasNota = useMemo(
    () => Boolean(refEfetiva) || hasStatusReal,
    [refEfetiva, hasStatusReal],
  );

  const isCancelada = useMemo(
    () => statusEfetivo.includes("cancel"),
    [statusEfetivo],
  );

  const isErro = useMemo(() => {
    return (
      statusEfetivo.includes("erro") ||
      statusEfetivo.includes("neg") ||
      statusEfetivo.includes("reje") ||
      statusEfetivo.includes("falha")
    );
  }, [statusEfetivo]);

  const [modalConfirmCancel, setModalConfirmCancel] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  const [modalConfigNotas, setModalConfigNotas] = useState(false);
  const [modalProcessing, setModalProcessing] = useState(false);

  const [modalViewNfse, setModalViewNfse] = useState(false);
  const [notaSelecionada, setNotaSelecionada] = useState<any | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const [modalPrevia, setModalPrevia] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [pendingFormData, setPendingFormData] = useState<Inputs | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const [cnaes, setCnaes] = useState<SelectOption[]>([]);
  const [isLoadingCnaes, setIsLoadingCnaes] = useState(false);

  const [itensServico, setItensServico] = useState<SelectOption[]>([]);
  const [isLoadingItens, setIsLoadingItens] = useState(false);

  const canCancelByStatus = useMemo(() => {
    const s = statusEfetivo;

    return s.includes("emit") || s.includes("autoriz");
  }, [statusEfetivo]);

  const isProcessing = useMemo(() => {
    return (
      statusEfetivo === "processando" ||
      statusEfetivo === "processando_autorizacao"
    );
  }, [statusEfetivo]);

  const disableCancel =
    !hasNota || !canCancelByStatus || isCancelada || isProcessing || isErro;
  const canCancel = !disableCancel && !isCanceling;

  const {
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors },
    getValues,
    watch,
    register,
  } = useForm<Inputs>({
    defaultValues: {
      iss_retido: false,
      valor_ir: "",
      valor_pis: "",
      valor_cofins: "",
      valor_csll: "",
      codigo_cnae: null,
      item_lista_servico: null,
      justificativa_cancelamento: "",
    },
  });

  const issRetidoValue = watch("iss_retido");

  const { refetch: refetchNfeByDesk, isFetching: isFetchingNfeByDesk } =
    useQuery<NfeResponse>({
      queryKey: ["NfeByDesk", id_desk],
      enabled: false,
      queryFn: async () => {
        const res = await NotasFiscaisService.getListNFes({
          pageNumber: 1,
          pageSize: 1,
          idDesk: Number(id_desk),
        });
        return res.data;
      },
      retry: 1,
      staleTime: 1000 * 30,
    });

  const fetchCnaes = useCallback(async () => {
    try {
      setIsLoadingCnaes(true);
      const res = await NotasFiscaisService.getCnaes();

      if (res?.status === 200 && Array.isArray(res?.data)) {
        const options: SelectOption[] = (res.data as CnaeApi[]).map((c) => ({
          value: String(c.codigo),
          label: `${c.codigo} - ${c.descricao}`,
        }));
        setCnaes(options);
        return;
      }

      toast.error("Não foi possível carregar os CNAEs");
    } catch {
      toast.error("Erro ao carregar CNAEs");
    } finally {
      setIsLoadingCnaes(false);
    }
  }, []);

  const formatCnaeForApi = (value?: string) =>
    value ? value.replace(/\D/g, "") : "";

  const fetchItensServico = useCallback(
    async (codigoCnae?: string) => {
      if (!codigoCnae) {
        setItensServico([]);
        setValue("item_lista_servico", null, { shouldValidate: true });
        return;
      }

      try {
        setIsLoadingItens(true);
        const res = await NotasFiscaisService.consultarItemServico(codigoCnae);

        if (res?.status === 200 && Array.isArray(res?.data)) {
          const options: SelectOption[] = res.data.map((i: any) => ({
            value: String(i.item_servico ?? "").replace(",", "."),
            label: `${String(i.item_servico ?? "").replace(",", ".")} - ${
              i.item_servico_descricao ?? ""
            }`,
          }));
          setItensServico(options);
          return;
        }

        setItensServico([]);
        toast.error("Não foi possível carregar os itens de serviço");
      } catch {
        setItensServico([]);
        toast.error("Erro ao carregar itens de serviço");
      } finally {
        setIsLoadingItens(false);
      }
    },
    [setValue],
  );

  useEffect(() => {
    if (!modalConfigNotas) return;
    fetchCnaes();
  }, [modalConfigNotas, fetchCnaes]);

  const justificativaValue = watch("justificativa_cancelamento") ?? "";
  const justificativaLen = justificativaValue.trim().length;
  const canConfirmCancel =
    !isCanceling && justificativaLen >= 15 && justificativaLen <= 255;

  const handleCancelNota = async () => {
    try {
      const justificativa = getValues("justificativa_cancelamento")?.trim();

      if (
        !justificativa ||
        justificativa.length < 15 ||
        justificativa.length > 255
      ) {
        toast.error("Informe uma justificativa entre 15 e 255 caracteres.");
        return;
      }

      setIsCanceling(true);

      await NotasFiscaisService.cancelarNFSE(id_desk, justificativa);

      toast.success("Nota cancelada com sucesso!");
      setStatusLocal("cancelado");
      setModalConfirmCancel(false);

      setValue("justificativa_cancelamento", "");

      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: ["ListNFes"] });
      queryClient.invalidateQueries({ queryKey: ["ListNFesTotal"] });
      queryClient.invalidateQueries({ queryKey: ["NfeByDesk", id_desk] });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.detalhes?.mensagem ?? "Erro ao cancelar nota",
      );
    } finally {
      setIsCanceling(false);
    }
  };

  const emitirNota = async (formData: Inputs) => {
    setIsLoading(true);

    const payload = {
      natureza_operacao: formData.natureza_operacao?.value,
      iss_retido: formData.iss_retido,
      ...(formData.iss_retido
        ? {
            valor_ir: formData.valor_ir,
            valor_pis: formData.valor_pis,
            valor_cofins: formData.valor_cofins,
            valor_csll: formData.valor_csll,
          }
        : {}),
      codigo_cnae: formData.codigo_cnae?.value,
      item_lista_servico: formData.item_lista_servico?.value,
      informacoes_adicionais_contribuinte:
        formData.informacoes_adicionais_contribuinte,
    };

    try {
      const res = await NotasFiscaisService.gerarNFse({
        id_desk,
        ...payload,
      });

      if (res?.status === 200) {
        setRefLocal(res.data?.ref ?? res.data?.ref_nfe ?? "pendente");

        setModalConfigNotas(false);
        setModalProcessing(true);

        reset();
        setPendingFormData(null);
        onSuccess?.();

        queryClient.invalidateQueries({ queryKey: ["jobs"] });
        queryClient.invalidateQueries({ queryKey: ["ListNFes"] });
        queryClient.invalidateQueries({ queryKey: ["ListNFesTotal"] });
        queryClient.invalidateQueries({ queryKey: ["NfeByDesk", id_desk] });

        return;
      }

      toast.error(res?.data?.msg ?? "Erro ao gerar nota fiscal");
    } catch (err: any) {
      toast.error(err?.data?.msg ?? "Erro ao gerar nota fiscal");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenPrevia: SubmitHandler<Inputs> = async (formData) => {
    setIsLoadingPreview(true);
    try {
      // 1. Empresa config
      const configRes = await NotasFiscaisService.getConfig_NFe();
      const empresaConfig = configRes?.status === 200 ? configRes.data : null;

      // 2. Tomador do deskData
      const cliente = deskData?.cliente ?? null;

      let tomadorEndereco = {
        endereco: cliente?.endereco as string | undefined,
        municipio: cliente?.municipio as string | undefined,
        uf: cliente?.uf as string | undefined,
        cep: cliente?.cep as string | undefined,
      };

      // 3. Se PJ, busca endereço via Brasil API
      if (cliente?.type_cliente !== "1" && cliente?.cnpj) {
        try {
          const cnpjClean = String(cliente.cnpj).replace(/\D/g, "");
          const cnpjRes = await GetAllClientes.consultarCnpj(cnpjClean);
          if (cnpjRes?.status === 200) {
            const d = cnpjRes.data?.data || cnpjRes.data;
            tomadorEndereco = {
              endereco:
                d?.logradouro || d?.endereco || tomadorEndereco.endereco,
              municipio: d?.municipio || d?.cidade || tomadorEndereco.municipio,
              uf: d?.uf || tomadorEndereco.uf,
              cep: d?.cep || tomadorEndereco.cep,
            };
          }
        } catch {
          // fallback para dados da desk
        }
      }

      // 4. Parsear proposta
      const propostaItems: Array<{
        descricao: string;
        quantidade: number;
        valor: number;
      }> = [];
      let total = 0;
      try {
        const proposta = JSON.parse(String(deskData?.proposta || "[]"));
        if (Array.isArray(proposta)) {
          proposta.forEach((item: any) => {
            const valor = Number(item.valor ?? item.price ?? item.value ?? 0);
            const quantidade = Number(
              item.quantidade ?? item.qty ?? item.qtd ?? 1,
            );
            propostaItems.push({
              descricao:
                item.descricao ??
                item.name ??
                item.titulo ??
                item.description ??
                "-",
              quantidade,
              valor,
            });
            total += valor;
          });
        }
      } catch {
        // proposta inválida, lista vazia
      }

      // 5. Montar prévia
      const preview: PreviewData = {
        emitente: {
          nome:
            empresaConfig?.nome_fantasia ?? empresaConfig?.razao_social ?? "-",
          cnpj: empresaConfig?.cnpj ?? "-",
          inscricao_municipal: empresaConfig?.inscricao_municipal,
          inscricao_estadual: empresaConfig?.inscricao_estadual,
          endereco: empresaConfig?.endereco,
          numero: empresaConfig?.numero,
          bairro: empresaConfig?.bairro,
          municipio: empresaConfig?.municipio,
          uf: empresaConfig?.uf,
          regime: empresaConfig?.optante_simples_nacional
            ? "Simples Nacional"
            : undefined,
        },
        tomador: {
          nome:
            cliente?.nome_completo ??
            cliente?.name_fantasy ??
            cliente?.nome ??
            "-",
          doc: cliente?.cnpj
            ? `CNPJ: ${cliente.cnpj}`
            : `CPF: ${cliente?.cpf ?? "-"}`,
          email: cliente?.email,
          telefone: cliente?.telefone,
          endereco: tomadorEndereco.endereco,
          municipio: tomadorEndereco.municipio,
          uf: tomadorEndereco.uf,
          cep: tomadorEndereco.cep,
        },
        servico: {
          items: propostaItems,
          total,
          natureza_operacao: formData.natureza_operacao?.label ?? "-",
          item_lista_servico: formData.item_lista_servico?.label ?? "-",
          codigo_cnae: formData.codigo_cnae?.label ?? "-",
          iss_retido: formData.iss_retido,
          valor_ir: formData.valor_ir,
          valor_pis: formData.valor_pis,
          valor_cofins: formData.valor_cofins,
          valor_csll: formData.valor_csll,
        },
      };

      setPendingFormData(formData);
      setPreviewData(preview);
      setModalPrevia(true);
    } catch {
      toast.error("Erro ao montar prévia da nota");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleConfirmPrevia = () => {
    if (!pendingFormData) return;
    setModalPrevia(false);
    emitirNota(pendingFormData);
  };

  const handleMainClick = async () => {
    if (!hasNota) {
      setModalConfigNotas(true);
      return;
    }

    const result = await refetchNfeByDesk();
    const nota = result.data?.data?.[0];

    if (!nota) {
      setNotaSelecionada({
        id_desk,
        ref_nfe: refEfetiva,
        status: statusLocal ?? status_nfe,
      });
      setModalViewNfse(true);
      return;
    }

    setNotaSelecionada(nota);
    setModalViewNfse(true);
  };

  const handleReenviarNota = () => {
    setModalViewNfse(false);
    setNotaSelecionada(null);
    reset();
    setItensServico([]);
    setModalConfigNotas(true);
  };

  const closeConfigModal = () => {
    reset();
    setItensServico([]);
    setPendingFormData(null);
    setPreviewData(null);
    setModalConfigNotas(false);
  };

  return (
    <>
      <div className="flex items-center">
        {variant === "button" ? (
          <button
            type="button"
            onClick={handleMainClick}
            disabled={isLoading || isFetchingNfeByDesk}
            className={`px-4 py-2 rounded text-white ${
              isLoading || isFetchingNfeByDesk
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-azul-400"
            }`}
          >
            {isFetchingNfeByDesk
              ? "Carregando..."
              : hasNota
                ? "Ver Nota"
                : isLoading
                  ? "Gerando..."
                  : "Gerar Nota"}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleMainClick}
            disabled={isLoading || isFetchingNfeByDesk}
            className={`h-9 w-9 inline-flex items-center justify-center rounded-md ${
              isLoading || isFetchingNfeByDesk
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-gray-100"
            }`}
            title={hasNota ? "Ver nota fiscal" : "Gerar nota fiscal"}
          >
            {hasNota ? (
              <span className="relative inline-flex">
                <FileSearch size={18} />
                {isErro && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500" />
                )}
              </span>
            ) : (
              <DollarSign size={18} />
            )}
          </button>
        )}

        <button
          type="button"
          onClick={() => canCancel && setModalConfirmCancel(true)}
          disabled={!canCancel}
          title={
            !hasNota
              ? "Nota ainda não emitida"
              : isCancelada
                ? "Nota já cancelada"
                : isErro
                  ? "Nota com erro — não é possível cancelar"
                  : "Cancelar nota"
          }
          className={`h-9 w-9 inline-flex items-center justify-center rounded-md transition ${
            canCancel
              ? "text-red-600 hover:bg-red-50"
              : "opacity-30 cursor-not-allowed"
          }`}
        >
          <FileX size={18} />
        </button>
      </div>

      {!!modalConfigNotas && (
        <ModalGeneric onClose={closeConfigModal} size="max-w-2xl w-full">
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-center">
              Configurações da nota fiscal
            </h2>

            <form
              onSubmit={(e) => e.preventDefault()}
              className="grid grid-cols-2 gap-4"
            >
              <div>
                <Controller
                  name="natureza_operacao"
                  control={control}
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      placeholder="Natureza da operação"
                      options={naturezaOperacao}
                      onChange={(e: any) =>
                        setValue("natureza_operacao", e, {
                          shouldValidate: true,
                        })
                      }
                    />
                  )}
                />
              </div>

              <div>
                <Controller
                  name="codigo_cnae"
                  control={control}
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      placeholder={
                        isLoadingCnaes
                          ? "Carregando CNAEs..."
                          : "Selecione o CNAE"
                      }
                      options={cnaes}
                      isSearchable
                      isClearable
                      isLoading={isLoadingCnaes}
                      onChange={(e: any) => {
                        setValue("codigo_cnae", e, { shouldValidate: true });
                        setValue("item_lista_servico", null, {
                          shouldValidate: true,
                        });
                        setItensServico([]);

                        const codigoFormatado = formatCnaeForApi(e?.value);
                        fetchItensServico(codigoFormatado);
                      }}
                      noOptionsMessage={() => "Nenhum CNAE encontrado"}
                    />
                  )}
                />
              </div>

              <div>
                <Controller
                  name="item_lista_servico"
                  control={control}
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      placeholder={
                        !control._formValues.codigo_cnae
                          ? "Selecione um CNAE primeiro"
                          : isLoadingItens
                            ? "Carregando itens..."
                            : "Selecione o item de serviço"
                      }
                      options={itensServico}
                      isSearchable
                      isClearable
                      isLoading={isLoadingItens}
                      isDisabled={!control._formValues.codigo_cnae || isLoading}
                      onChange={(e: any) =>
                        setValue("item_lista_servico", e, {
                          shouldValidate: true,
                        })
                      }
                      noOptionsMessage={() =>
                        !control._formValues.codigo_cnae
                          ? "Selecione um CNAE"
                          : "Nenhum item encontrado"
                      }
                    />
                  )}
                />
              </div>

              {/* ISS Retido */}
              <div className="col-span-2 flex items-center gap-3 rounded-xl border border-cinza-100 bg-cinza-50 px-4 py-3">
                <input
                  id="iss_retido"
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer accent-azul-400"
                  {...register("iss_retido")}
                />
                <label
                  htmlFor="iss_retido"
                  className="cursor-pointer select-none text-sm font-medium text-cinza-700"
                >
                  ISS Retido
                </label>
              </div>

              {issRetidoValue && (
                <>
                  <div className="col-span-2">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-cinza-500">
                      Valores de retenção
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-xs text-cinza-600">
                          Imposto de Renda (IR)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className="w-full rounded border border-cinza-200 px-3 py-2 text-sm outline-none focus:border-azul-400"
                          {...register("valor_ir", {
                            required: issRetidoValue
                              ? "Campo obrigatório"
                              : false,
                          })}
                        />
                        {errors.valor_ir && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.valor_ir.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="mb-1 block text-xs text-cinza-600">
                          PIS
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className="w-full rounded border border-cinza-200 px-3 py-2 text-sm outline-none focus:border-azul-400"
                          {...register("valor_pis", {
                            required: issRetidoValue
                              ? "Campo obrigatório"
                              : false,
                          })}
                        />
                        {errors.valor_pis && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.valor_pis.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="mb-1 block text-xs text-cinza-600">
                          COFINS
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className="w-full rounded border border-cinza-200 px-3 py-2 text-sm outline-none focus:border-azul-400"
                          {...register("valor_cofins", {
                            required: issRetidoValue
                              ? "Campo obrigatório"
                              : false,
                          })}
                        />
                        {errors.valor_cofins && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.valor_cofins.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="mb-1 block text-xs text-cinza-600">
                          CSLL
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className="w-full rounded border border-cinza-200 px-3 py-2 text-sm outline-none focus:border-azul-400"
                          {...register("valor_csll", {
                            required: issRetidoValue
                              ? "Campo obrigatório"
                              : false,
                          })}
                        />
                        {errors.valor_csll && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.valor_csll.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="col-span-2 flex justify-end">
                <button
                  className={`inline-flex rounded-3xl py-2 px-8 text-white font-semibold ${
                    isLoadingPreview
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-azul-400"
                  }`}
                  type="button"
                  disabled={isLoadingPreview}
                  onClick={handleSubmit(handleOpenPrevia)}
                >
                  {isLoadingPreview ? "Carregando..." : "Prévia da Nota"}
                </button>
              </div>
            </form>
          </div>
        </ModalGeneric>
      )}

      {!!modalProcessing && (
        <ModalGeneric
          onClose={() => setModalProcessing(false)}
          size="max-w-md w-full"
        >
          <div className="space-y-4 text-center">
            <h2 className="text-lg font-bold">Nota enviada com sucesso!</h2>
            <p className="text-sm text-gray-600">
              Sua nota foi enviada e está em processamento. Você será avisado
              assim que ela for aprovada ou negada.
            </p>
            <button
              onClick={() => setModalProcessing(false)}
              className="bg-azul-400 inline-flex rounded-3xl py-2 px-8 text-white font-semibold"
            >
              Entendido
            </button>
          </div>
        </ModalGeneric>
      )}

      {!!modalConfirmCancel && (
        <ModalGeneric
          onClose={() => setModalConfirmCancel(false)}
          size="max-w-md w-full"
        >
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-center">Cancelar nota</h2>
            <p className="text-sm text-gray-600 text-center">
              Tem certeza que deseja cancelar esta nota fiscal? Essa ação não
              pode ser desfeita.
            </p>

            <div>
              <Controller
                name="justificativa_cancelamento"
                control={control}
                rules={{
                  required: "Justificativa é obrigatória",
                  minLength: { value: 15, message: "Mínimo de 15 caracteres" },
                  maxLength: {
                    value: 255,
                    message: "Máximo de 255 caracteres",
                  },
                  validate: (v) =>
                    v?.trim().length >= 15 ? true : "Mínimo de 15 caracteres",
                }}
                render={({ field }) => (
                  <>
                    <label className="text-sm font-medium">
                      Justificativa do cancelamento
                    </label>
                    <textarea
                      {...field}
                      rows={4}
                      maxLength={255}
                      placeholder="Descreva o motivo do cancelamento (mín. 15 caracteres)"
                      className={`mt-1 w-full rounded border p-2 text-sm outline-none ${
                        errors?.justificativa_cancelamento
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      disabled={isCanceling}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-xs text-red-600">
                        {errors?.justificativa_cancelamento?.message as any}
                      </span>
                      <span className="text-xs text-gray-500">
                        {field.value?.length ?? 0}/255
                      </span>
                    </div>
                  </>
                )}
              />
            </div>

            <div className="flex justify-center gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded border"
                onClick={() => setModalConfirmCancel(false)}
                disabled={isCanceling}
              >
                Voltar
              </button>

              <button
                type="button"
                className={`px-4 py-2 rounded text-white ${
                  !canConfirmCancel
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
                onClick={handleCancelNota}
                disabled={!canConfirmCancel}
              >
                {isCanceling ? "Cancelando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </ModalGeneric>
      )}

      <ViewNfseDetail
        isOpen={modalViewNfse}
        onClose={() => setModalViewNfse(false)}
        nota={notaSelecionada}
        onReenviar={handleReenviarNota}
        onUpdateNota={(patch) => {
          setNotaSelecionada((prev: any) => ({ ...(prev ?? {}), ...patch }));
          if (patch?.status) setStatusLocal(patch.status);
        }}
      />

      <ModalPreviaNotaFiscal
        isOpen={modalPrevia}
        onClose={() => setModalPrevia(false)}
        onConfirm={handleConfirmPrevia}
        isLoading={isLoading}
        data={previewData}
      />
    </>
  );
}
